import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Lock, ArrowRight, Check } from 'lucide-react';
import { AuthShell, HudLabel, HudInput, PrimaryButton, SocialButton, AuthNotice, passwordStrength, C } from '@/components/auth/AuthUI';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Best-effort silent location → NZ climate zone (never blocks sign-up).
function getCoords() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({}),
      { timeout: 6000, maximumAge: 600000 },
    );
  });
}

export default function SignUp() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [touched, setTouched] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);

  const strength = passwordStrength(password);
  const nameOk = name.trim().length > 0;
  const emailOk = EMAIL_RE.test(email.trim());
  const pwOk = password.length >= 8;
  const confirmOk = confirm.length > 0 && confirm === password;
  const canSubmit = nameOk && emailOk && pwOk && confirmOk && terms;

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) { if (!terms) setError('Please accept the Terms to continue.'); return; }
    setBusy(true); setError('');
    const parts = name.trim().split(/\s+/);
    const coords = await getCoords();
    const payload = {
      first_name: parts[0], last_name: parts.slice(1).join(' '),
      email: email.trim().toLowerCase(), password, terms_accepted: true,
      country: 'New Zealand', ...coords,
    };
    try {
      await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      navigate('/dashboard');
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "We couldn't create your account. Please try again.";
      setError(msg);
      setBusy(false);
    }
  };

  const googleSignUp = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setGoogleBusy(true);
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const appleSignUp = () => { setAppleBusy(true); setTimeout(() => { setNotice('Sign up with Apple opens in the Homegrown iOS app.'); setAppleBusy(false); }, 550); };

  return (
    <AuthShell subtitle="Create your account" logoTestId="signup-sprout">
      {notice ? <div className="mb-4"><AuthNotice kind="success" dataTestid="signup-notice">{notice}</AuthNotice></div> : null}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <HudLabel>Full name</HudLabel>
          <HudInput icon={User} placeholder="Jamie Green" autoCapitalize="words"
            value={name} onChange={(e) => setName(e.target.value)} dataTestid="signup-name-input" />
          {touched && !nameOk ? <p className="text-xs mt-1" style={{ color: C.terracotta }}>Please enter your name.</p> : null}
        </div>
        <div>
          <HudLabel>Email</HudLabel>
          <HudInput icon={Mail} type="email" autoComplete="email" placeholder="you@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)} dataTestid="signup-email-input" />
          {touched && !emailOk ? <p className="text-xs mt-1" style={{ color: C.terracotta }}>Please enter a valid email.</p> : null}
        </div>
        <div>
          <HudLabel>Password</HudLabel>
          <HudInput icon={Lock} secure autoComplete="new-password" placeholder="At least 8 characters"
            value={password} onChange={(e) => setPassword(e.target.value)} dataTestid="signup-password-input" />
          {password ? (
            <div className="mt-2" data-testid="signup-strength">
              <div className="flex gap-1.5">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className="flex-1 rounded-full transition-all" style={{
                    height: 4, background: i < strength.score ? strength.color : 'rgba(250,246,236,0.14)',
                    boxShadow: i < strength.score ? `0 0 8px ${strength.color}` : 'none' }} />
                ))}
              </div>
              <p className="text-xs mt-1 font-semibold" style={{ color: strength.color }}>{strength.label}</p>
            </div>
          ) : null}
        </div>
        <div>
          <HudLabel>Confirm password</HudLabel>
          <HudInput icon={Lock} secure autoComplete="new-password" placeholder="Re-enter your password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} dataTestid="signup-confirm-input" />
          {touched && !confirmOk ? <p className="text-xs mt-1" style={{ color: C.terracotta }}>Passwords don't match.</p> : null}
        </div>

        <button type="button" onClick={() => { setTerms((t) => !t); setError(''); }} data-testid="signup-terms"
          className="flex items-start gap-2.5 text-left text-sm pt-1" style={{ color: 'rgba(250,246,236,0.78)' }}>
          <span className="flex items-center justify-center rounded-md shrink-0 transition-all mt-0.5" style={{
            width: 20, height: 20, background: terms ? C.lime : 'transparent',
            border: `1.5px solid ${terms ? C.lime : 'rgba(250,246,236,0.35)'}` }}>
            {terms ? <Check className="w-3.5 h-3.5" style={{ color: C.forestDeep }} /> : null}
          </span>
          <span>I agree to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid="signup-terms-link"
              className="font-semibold hover:underline" style={{ color: C.lime }}>Terms and Conditions</a> and{' '}
            <a href="/privacy" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid="signup-privacy-link"
              className="font-semibold hover:underline" style={{ color: C.lime }}>Privacy Policy</a>.</span>
        </button>

        {error ? <AuthNotice dataTestid="signup-error"><span className="inline-flex items-center gap-2"><Lock className="w-4 h-4" />{error}</span></AuthNotice> : null}

        <PrimaryButton type="submit" disabled={busy} dataTestid="signup-submit">
          {busy ? 'Creating your account…' : 'Create account'} <ArrowRight className="w-4 h-4" />
        </PrimaryButton>

        <div className="flex items-center gap-3 py-1">
          <span className="flex-1 h-px" style={{ background: 'rgba(250,246,236,0.14)' }} />
          <span className="text-xs" style={{ color: 'rgba(250,246,236,0.45)' }}>or sign up with</span>
          <span className="flex-1 h-px" style={{ background: 'rgba(250,246,236,0.14)' }} />
        </div>
        <div className="space-y-2.5">
          <SocialButton provider="apple" label="Sign up with Apple" onClick={appleSignUp} loading={appleBusy} dataTestid="signup-apple-button" />
          <SocialButton provider="google" label="Sign up with Google" onClick={googleSignUp} loading={googleBusy} dataTestid="signup-google-button" />
        </div>

        <p className="text-center text-sm pt-2" style={{ color: 'rgba(250,246,236,0.7)' }}>
          Already have an account?{' '}
          <button type="button" onClick={() => navigate('/login')} data-testid="signup-signin-link"
            className="font-bold hover:underline" style={{ color: C.lime }}>Sign in</button>
        </p>
      </form>
    </AuthShell>
  );
}
